import { Group, NumberInput, Stack, Textarea, TextInput } from '@mantine/core';
import { useIntl } from 'react-intl';

import { handlingUnitsStepMessages } from './handling-units-step-messages';
import { dotKeyToHazardClasses, HAZARD_CLASS_OPTIONS, hazardClassesToDotKey, PACKING_GROUP_OPTIONS } from './hazmat-options';
import { Dropdown } from '../../../common/Dropdown';

import type { FreightHazmatInfo, HazmatIdType, LtlCommodity, PackingGroup } from '../../../../types/ltl';

export type CommodityHazmatInfoProps = {
  commodity: LtlCommodity;
  index: number;
  onChange: (next: LtlCommodity) => void;
  /**
   * Formatted inline error messages keyed by field path, already sliced to this commodity's
   * `hazmatInfo` prefix (e.g. `'identificationNumber'`, `'emergencyPhone'`).
   */
  fieldErrors?: Record<string, string>;
  /** When false, hides optional fields (ERG reference, flashpoint, additional details). Defaults to true. */
  showOptionalFields?: boolean;
};

const ID_NUMBER_MAX_LENGTH = 30;
const PROPER_SHIPPING_NAME_MAX_LENGTH = 100;
const EMERGENCY_CONTACT_NAME_MAX_LENGTH = 30;
const EMERGENCY_PHONE_MAX_LENGTH = 30;
const ERG_REFERENCE_MAX_LENGTH = 4;
const FLASHPOINT_MAX_TEMP_F = 999;
const ADDITIONAL_DETAILS_MAX_LENGTH = 250;

/**
 * Derives `hazIdType` from the identification-number prefix (case-insensitive).
 * "NA…" → NA; anything else (including "UN…", no prefix, bare digits) → UN.
 * Returns null only for an empty value.
 */
const deriveHazIdType = (identificationNumber: string): HazmatIdType | null => {
  if (!identificationNumber) return null;
  if (/^na/i.test(identificationNumber)) return 'NA';
  return 'UN';
};

/**
 * The "Show hazardous material" reveal block for a commodity card.
 * Collects hazmat details that map to the backend `FreightHazmatInfo` contract.
 * A single "UN / NA Number" field accepts the full identification number with its
 * prefix; `hazIdType` is derived from the prefix (case-insensitive "NA…" → NA,
 * everything else → UN).
 */
export const CommodityHazmatInfo = ({ commodity, index, onChange, fieldErrors, showOptionalFields = true }: CommodityHazmatInfoProps) => {
  const intl = useIntl();
  const info = commodity.hazmatInfo;

  const buildNext = (patch: Partial<FreightHazmatInfo>): FreightHazmatInfo => {
    const base: FreightHazmatInfo = {
      hazIdType: info?.hazIdType ?? null,
      identificationNumber: info?.identificationNumber ?? null,
      properShippingName: info?.properShippingName ?? null,
      hazardClass: info?.hazardClass ?? null,
      packingGroup: info?.packingGroup ?? null,
      emergencyPhone: info?.emergencyPhone ?? null,
      emergencyContactName: info?.emergencyContactName ?? null,
      emergencyResponseReference: info?.emergencyResponseReference ?? null,
      flashpointTemperatureF: info?.flashpointTemperatureF ?? null,
      additionalDetails: info?.additionalDetails ?? null,
    };
    return { ...base, ...patch };
  };

  const handleIdentificationNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    const nextInfo = buildNext({ identificationNumber: value || null, hazIdType: deriveHazIdType(value) });
    onChange({ ...commodity, hazmatInfo: nextInfo });
  };

  const handleProperShippingNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextInfo = buildNext({ properShippingName: event.currentTarget.value || null });
    onChange({ ...commodity, hazmatInfo: nextInfo });
  };

  const handleHazardClassChange = (value: string | null) => {
    const nextInfo = buildNext({ hazardClass: dotKeyToHazardClasses(value) });
    onChange({ ...commodity, hazmatInfo: nextInfo });
  };

  const handlePackingGroupChange = (value: string | null) => {
    const nextInfo = buildNext({ packingGroup: (value as PackingGroup) ?? null });
    onChange({ ...commodity, hazmatInfo: nextInfo });
  };

  const handleEmergencyContactNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextInfo = buildNext({ emergencyContactName: event.currentTarget.value || null });
    onChange({ ...commodity, hazmatInfo: nextInfo });
  };

  const handleEmergencyPhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextInfo = buildNext({ emergencyPhone: event.currentTarget.value || null });
    onChange({ ...commodity, hazmatInfo: nextInfo });
  };

  const handleErgReferenceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextInfo = buildNext({ emergencyResponseReference: event.currentTarget.value || null });
    onChange({ ...commodity, hazmatInfo: nextInfo });
  };

  const handleFlashpointChange = (value: number | string) => {
    const parsed = typeof value === 'string' ? parseFloat(value) : value;
    const clamped = Number.isNaN(parsed) ? null : Math.min(parsed, FLASHPOINT_MAX_TEMP_F);
    const nextInfo = buildNext({ flashpointTemperatureF: clamped });
    onChange({ ...commodity, hazmatInfo: nextInfo });
  };

  const handleAdditionalDetailsChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextInfo = buildNext({ additionalDetails: event.currentTarget.value || null });
    onChange({ ...commodity, hazmatInfo: nextInfo });
  };

  return (
    <Stack gap='sm'>
      <TextInput
        data-testid={`commodity-hazmat-id-number-${index}`}
        error={fieldErrors?.['identificationNumber']}
        label={intl.formatMessage(handlingUnitsStepMessages.hazmatIdNumberLabel)}
        maxLength={ID_NUMBER_MAX_LENGTH}
        onChange={handleIdentificationNumberChange}
        required
        value={info?.identificationNumber ?? ''}
      />
      <TextInput
        data-testid={`commodity-hazmat-shipping-name-${index}`}
        error={fieldErrors?.['properShippingName']}
        label={intl.formatMessage(handlingUnitsStepMessages.hazmatProperShippingNameLabel)}
        maxLength={PROPER_SHIPPING_NAME_MAX_LENGTH}
        onChange={handleProperShippingNameChange}
        required
        value={info?.properShippingName ?? ''}
      />
      <Group align='flex-start' grow>
        <Dropdown
          clearable
          data={HAZARD_CLASS_OPTIONS}
          data-testid={`commodity-hazmat-class-${index}`}
          error={fieldErrors?.['hazardClass']}
          label={intl.formatMessage(handlingUnitsStepMessages.hazmatHazardClassLabel)}
          onChange={handleHazardClassChange}
          placeholder={intl.formatMessage(handlingUnitsStepMessages.hazmatHazardClassPlaceholder)}
          required
          value={hazardClassesToDotKey(info?.hazardClass)}
        />
        <Dropdown
          clearable
          data={PACKING_GROUP_OPTIONS}
          data-testid={`commodity-hazmat-packing-group-${index}`}
          error={fieldErrors?.['packingGroup']}
          label={intl.formatMessage(handlingUnitsStepMessages.hazmatPackingGroupLabel)}
          onChange={handlePackingGroupChange}
          placeholder={intl.formatMessage(handlingUnitsStepMessages.hazmatPackingGroupPlaceholder)}
          required
          value={info?.packingGroup ?? null}
        />
      </Group>
      <Group align='flex-start' grow>
        <TextInput
          data-testid={`commodity-hazmat-emergency-name-${index}`}
          error={fieldErrors?.['emergencyContactName']}
          label={intl.formatMessage(handlingUnitsStepMessages.hazmatEmergencyContactNameLabel)}
          maxLength={EMERGENCY_CONTACT_NAME_MAX_LENGTH}
          onChange={handleEmergencyContactNameChange}
          required
          value={info?.emergencyContactName ?? ''}
        />
        <TextInput
          data-testid={`commodity-hazmat-emergency-phone-${index}`}
          error={fieldErrors?.['emergencyPhone']}
          label={intl.formatMessage(handlingUnitsStepMessages.hazmatEmergencyPhoneLabel)}
          maxLength={EMERGENCY_PHONE_MAX_LENGTH}
          onChange={handleEmergencyPhoneChange}
          required
          value={info?.emergencyPhone ?? ''}
        />
      </Group>
      {showOptionalFields && (
        <>
          <Group align='flex-start' grow>
            <TextInput
              data-testid={`commodity-hazmat-erg-${index}`}
              label={intl.formatMessage(handlingUnitsStepMessages.hazmatErgReferenceLabel)}
              maxLength={ERG_REFERENCE_MAX_LENGTH}
              onChange={handleErgReferenceChange}
              value={info?.emergencyResponseReference ?? ''}
            />
            <NumberInput
              allowNegative={false}
              data-testid={`commodity-hazmat-flashpoint-${index}`}
              label={intl.formatMessage(handlingUnitsStepMessages.hazmatFlashpointLabel)}
              max={FLASHPOINT_MAX_TEMP_F}
              min={0}
              onChange={handleFlashpointChange}
              value={info?.flashpointTemperatureF ?? ''}
            />
          </Group>
          <Textarea
            autosize
            data-testid={`commodity-hazmat-additional-${index}`}
            label={intl.formatMessage(handlingUnitsStepMessages.hazmatAdditionalDetailsLabel)}
            maxLength={ADDITIONAL_DETAILS_MAX_LENGTH}
            minRows={2}
            onChange={handleAdditionalDetailsChange}
            placeholder={intl.formatMessage(handlingUnitsStepMessages.hazmatAdditionalDetailsPlaceholder)}
            value={info?.additionalDetails ?? ''}
          />
        </>
      )}
    </Stack>
  );
};
